import { useNavigation } from '@react-navigation/native'
import { Button, Center, Flex, Heading, ScrollView, Text, useBreakpointValue, VStack } from 'native-base'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AppLoading } from '../../components/AppLoading'
import HeaderLogo from '../../components/HeaderLogo/HeaderLogo'
import { ReferralImage } from '../../components/images/Referral'
import { useAuth } from '../../context/auth'
import { useConfig } from '../../context/config'
import { useReferral } from '../../data/use-referral'
import { saveReferralSlug } from '../../lib/referral'
import { RootStackScreenProps } from '../../navigation'
import { REDIRECTION_REASON_REFERRAL, REDIRECTION_REASON_REFERRAL_ERROR } from '../Welcome/redirect-reasons'
import { WelcomeImageError } from '../Welcome/WelcomeImage'

type Props = RootStackScreenProps<'Referral'>

function LoggedInView({ campaignName, error }: { campaignName?: string; error?: Error }) {
  const { t } = useTranslation()
  const navigation = useNavigation<Props['navigation']>()
  const { config } = useConfig()

  const handleGoToHomepage = useCallback(() => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'Main', params: { screen: 'HomeTab', params: { screen: 'HowCanWeHelp' } } }],
    })
  }, [navigation])

  return (
    <Flex alignItems="center" textAlign="center" flex={1} mt={6}>
      {error ? <WelcomeImageError /> : <ReferralImage alt={t('screens.referral.loggedIn.heading')} />}
      <Heading
        color={'neutral.900'}
        fontSize={'20px'}
        accessibilityRole="header"
        accessibilityLevel={1}
        mb={4}
        testID="loggedIn-heading"
      >
        {t(error ? 'screens.referral.expired.heading' : 'screens.referral.loggedIn.heading')}
      </Heading>
      <Text fontSize="md" color="gray.700" mt={4}>
        {!error && campaignName && t('screens.referral.loggedIn.text', { code: campaignName })}
        {error && t('screens.referral.loggedIn.codeExpired', { partnerName: config.partnerName })}
      </Text>
      <Flex flex={1} />
      <Button onPress={handleGoToHomepage} width="full">
        {t('screens.referral.loggedIn.button')}
      </Button>
    </Flex>
  )
}

export const Referral: React.FC<Props> = ({ navigation, route }) => {
  const { campaign, slug, error, isLoading } = useReferral(route.params.slug)
  const auth = useAuth()
  const largeScreenStyles = useBreakpointValue({
    md: { width: '600px', marginLeft: '50%', transform: 'translateX(-50%)' },
  })
  const contentContainerStyle = useMemo(
    () => ({ maxWidth: '600px', padding: '16px', backgroundColor: 'white', flexGrow: 1, ...largeScreenStyles }),
    [largeScreenStyles]
  )

  const redirectToWelcome = useCallback((reason: string) => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'Welcome', params: { reason } }],
    })
  }, [])

  useEffect(() => {
    if (!auth.isLoggedIn && error?.error) {
      redirectToWelcome(REDIRECTION_REASON_REFERRAL_ERROR)
    }
  }, [error])

  useEffect(() => {
    if (!auth.isLoggedIn && slug) {
      saveReferralSlug(slug)
      redirectToWelcome(REDIRECTION_REASON_REFERRAL)
    }
  }, [slug])

  if (isLoading) {
    return <AppLoading />
  }

  return (
    <ScrollView _contentContainerStyle={contentContainerStyle}>
      <VStack height={'100%'} justifyContent={'space-between'}>
        <Center>{<HeaderLogo />}</Center>
        {error && campaign && <LoggedInView campaignName={campaign.name} error={error} />}
        {!error && campaign && slug && auth.isLoggedIn && <LoggedInView campaignName={campaign.name} />}
      </VStack>
    </ScrollView>
  )
}

export default Referral
